import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"

function formatLapTimeMs(ms) {
  const totalSeconds = Math.floor(ms / 1000)
  const millis = ms % 1000
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`
  }
  return `${seconds}.${String(millis).padStart(3, "0")}`
}

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    track: String,
    color: String,
    bestMs: Number,
    points: Array,
    improving: Boolean,
    deltaMs: Number,
  }

  connect() {
    this.render()
    this.resizeObserver = new ResizeObserver(() => this.render())
    this.resizeObserver.observe(this.canvasTarget)
    this._onAppearanceChange = () => this.render()
    window.addEventListener("appearance:change", this._onAppearanceChange)
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  _cssVar(name) {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim()
  }

  render() {
    const container = this.canvasTarget
    container.innerHTML = ""
    container.style.position = "relative"

    const rawPoints = this.pointsValue
    if (!rawPoints || rawPoints.length < 2) return

    const color = this.colorValue || "#6366f1"
    const points = rawPoints.map((d) => ({
      date: new Date(d.date),
      time_ms: d.time_ms,
      session_id: d.session_id,
    }))

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const margin = { top: 4, right: 4, bottom: 20, left: 4 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const xScale = d3
      .scalePoint()
      .domain(d3.range(points.length))
      .range([0, innerWidth])

    const timeExtent = d3.extent(points, (d) => d.time_ms)
    const timePad = (timeExtent[1] - timeExtent[0]) * 0.15 || 500
    const yScale = d3
      .scaleLinear()
      .domain([timeExtent[0] - timePad, timeExtent[1] + timePad])
      .range([0, innerHeight])

    const svg = d3
      .select(container)
      .append("svg")
      .attr("width", width)
      .attr("height", height)

    const defs = svg.append("defs")

    const gradientId = `sm-grad-${this.track || Math.random().toString(36).slice(2)}`
    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%")

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0.25)

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", color)
      .attr("stop-opacity", 0.03)

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)

    // X axis — evenly spaced date ticks (show up to 5)
    const tickIndices =
      points.length <= 5
        ? d3.range(points.length)
        : d3.ticks(0, points.length - 1, 4).map(Math.round)
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(tickIndices)
          .tickSize(0)
          .tickFormat((i) => d3.timeFormat("%b %-d")(points[i].date)),
      )
      .call((sel) => sel.select(".domain").remove())
      .call((sel) =>
        sel
          .selectAll(".tick text")
          .attr("fill", this._cssVar("--app-text-muted"))
          .attr("font-size", "9px"),
      )

    const area = d3
      .area()
      .x((d, i) => xScale(i))
      .y0(innerHeight)
      .y1((d) => yScale(d.time_ms))
      .curve(d3.curveMonotoneX)

    const line = d3
      .line()
      .x((d, i) => xScale(i))
      .y((d) => yScale(d.time_ms))
      .curve(d3.curveMonotoneX)

    // Area fill
    g.append("path")
      .datum(points)
      .attr("fill", `url(#${gradientId})`)
      .attr("d", area)

    // Line
    g.append("path")
      .datum(points)
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 1.5)
      .attr("d", line)

    // Tooltip
    const tooltip = d3
      .select(container)
      .append("div")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("background", this._cssVar("--app-text"))
      .style("color", this._cssVar("--app-surface"))
      .style("padding", "6px 10px")
      .style("border-radius", "6px")
      .style("font-size", "11px")
      .style("line-height", "1.4")
      .style("opacity", 0)
      .style("z-index", 10)
      .style("white-space", "nowrap")

    // Dots
    g.selectAll(".dot")
      .data(points)
      .join("circle")
      .attr("cx", (d, i) => xScale(i))
      .attr("cy", (d) => yScale(d.time_ms))
      .attr("r", 3)
      .attr("fill", this._cssVar("--app-surface"))
      .attr("stroke", color)
      .attr("stroke-width", 1.5)
      .style("cursor", "pointer")
      .on("mouseenter", (event, d) => {
        tooltip
          .html(
            `<strong>${formatLapTimeMs(d.time_ms)}</strong><br><span style="opacity:0.7">${d3.timeFormat("%b %-d, %Y")(d.date)}</span>`,
          )
          .style("opacity", 1)
      })
      .on("mousemove", (event) => {
        const rect = container.getBoundingClientRect()
        tooltip
          .style("left", `${event.clientX - rect.left + 10}px`)
          .style("top", `${event.clientY - rect.top - 30}px`)
      })
      .on("mouseleave", () => {
        tooltip.style("opacity", 0)
      })
      .on("click", (event, d) => {
        window.Turbo.visit(`/sessions/${d.session_id}`)
      })
  }
}
